import React, { useState } from "react";
import posthog from "posthog-js";
import { faFileAlt, faFileImage } from "@fortawesome/free-regular-svg-icons";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FileProps, translate } from "../..";
import type { AppDispatch } from "../../app";
import {
  allowedExtensions,
  imageFileExtensions,
} from "../../config/allowedFileExtensions";
import FileAttachDropzone from "./components/FileAttachDropzone";
import FileAttachErrorBox from "./components/FileAttachErrorBox";
import FileAttachList from "./components/FileAttachList";
import { FileUploadProps } from "./fileUploadTypes";
import { caseToOperateThunks } from "../../redux/cto/caseToOperate";
import "./style.scss";

interface FileAttachProps {
  name: string;
  value: FileProps[];
  onChange: (filesIds: number[]) => void;
  vesselId: string;
  ctoId: string;
  uploadFiles: (
    files: File[],
    onUploadSuccess?: () => void,
  ) => (dispatch: Dispatch<AnyAction>) => Promise<FileProps[]>;
  setIsLoading: (isUploading: boolean) => void;
  downloadFile: (file: FileProps) => (dispatch: Dispatch) => void;
}

const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES = 5;

const getFileIcon = (fileName: string, hasError: boolean) => {
  const extension = fileName.split(".").pop();
  const isImageExtension =
    extension && imageFileExtensions.includes(extension.toLowerCase());
  if (hasError) {
    return (
      <FontAwesomeIcon
        className="file-attach__icon file-attach__icon--error"
        icon={faExclamationCircle}
        aria-label="File error"
      />
    );
  }
  return (
    <FontAwesomeIcon
      className="file-attach__icon"
      icon={isImageExtension ? faFileImage : faFileAlt}
      aria-label={isImageExtension ? "Image file" : "File"}
    />
  );
};

export const FileAttach: React.FC<FileAttachProps> = ({
  name,
  value,
  onChange,
  vesselId,
  ctoId,
  uploadFiles,
  downloadFile,
  setIsLoading,
}: FileAttachProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [files, setFiles] = useState<FileUploadProps[]>([...value]);
  const [hasExceededFileLimit, setHasExceededFileLimit] =
    useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDownload = async (file: FileProps) => {
    setDownloadingId(file.attachment_id);
    await downloadFile(file)(dispatch);
    setDownloadingId(-1);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.dataTransfer.files &&
      e.dataTransfer.files.length > 0 &&
      !isLimitReached
    ) {
      handleChangeWithUpload(e.dataTransfer.files);
    }
  };
  const handleDropzoneClick = () => {
    if (!isLimitReached && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && !isLimitReached) {
      handleChangeWithUpload(e.target.files);
    }
  };

  const handleChange = (newFiles: FileUploadProps[]) => {
    setFiles(newFiles);
    onChange(newFiles.map(({ attachment_id }) => attachment_id));
  };

  const validateFiles = (
    filesToUpload: FileList,
  ): { valid: File[]; errors: FileUploadProps[] } => {
    const errors: FileUploadProps[] = [];
    const valid: File[] = [];
    Array.from(filesToUpload).forEach((file) => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!allowedExtensions.includes(ext || "")) {
        errors.push({
          attachment_id: -1,
          file_name: file.name,
          status: "unsupported",
        });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push({
          attachment_id: -1,
          file_name: file.name,
          status: "size-exceeded",
        });
        return;
      }
      valid.push(file);
    });
    return { valid, errors };
  };

  const handleChangeWithUpload = async (filesToUpload: FileList) => {
    const oldFiles = [...files];
    setHasExceededFileLimit(false);
    if (oldFiles.length + filesToUpload.length > MAX_FILES) {
      setHasExceededFileLimit(true);
      return;
    }
    const { valid, errors } = validateFiles(filesToUpload);
    if (errors.length) {
      setFiles([...oldFiles, ...errors]);
    }
    if (!valid.length) return;
    const onUploadSuccess = () => {
      toast.success(translate("ctoPage.riskMatrix.uploadFileSuccess"), {
        closeButton: true,
        draggable: false,
      });
    };
    try {
      setIsLoading(true);
      setFiles([
        ...oldFiles,
        ...valid.map((file) => ({
          attachment_id: -1,
          file_name: file.name,
          isLoading: true,
          status: "uploading" as FileUploadProps["status"],
        })),
      ]);
      const uploadedFiles = await uploadFiles(valid, onUploadSuccess)(dispatch);
      handleChange([
        ...oldFiles,
        ...uploadedFiles.map((f) => ({
          ...f,
          status: "completed" as FileUploadProps["status"],
        })),
        ...errors,
      ]);
    } catch (error) {
      posthog.captureException(error);
      setFiles([
        ...oldFiles,
        ...valid.map((file) => ({
          attachment_id: -1,
          file_name: file.name,
          error: "Upload Error",
          status: "error" as FileUploadProps["status"],
        })),
        ...errors,
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeWithDelete = async (
    fileId: number,
    indexToDelete: number,
    vesselId: string,
    ctoId: string,
  ) => {
    const oldFiles = [...files];

    const onDeleteSuccess = () => {
      toast.success(translate("ctoPage.riskMatrix.deleteFileSuccess"), {
        closeButton: true,
        draggable: false,
      });
    };

    try {
      const tempFiles = files.flatMap((file) => ({
        ...file,
        isLoading: false,
      }));
      tempFiles[indexToDelete].isLoading = true;
      setFiles(tempFiles);
      setIsLoading(true);
      if (tempFiles[indexToDelete].status !== "unsupported" && fileId !== -1) {
        await caseToOperateThunks.deleteFile(
          vesselId,
          ctoId,
          fileId,
          onDeleteSuccess,
        )(dispatch);
      }
      handleChange(
        oldFiles.filter(({ attachment_id }) => attachment_id !== fileId),
      );
    } catch (error) {
      posthog.captureException(error);
      setFiles(oldFiles);
    } finally {
      setIsLoading(false);
    }
  };

  const isLimitReached = files.length >= MAX_FILES;

  return (
    <div className="file-attach file-attach--horizontal">
      <div className="file-attach__main-row">
        <FileAttachDropzone
          name={name}
          isLimitReached={isLimitReached}
          inputRef={inputRef}
          handleDropzoneClick={handleDropzoneClick}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          handleInputChange={handleInputChange}
          maxFileSizeMB={MAX_FILE_SIZE_MB}
          maxFiles={MAX_FILES}
        />
        <FileAttachList
          files={files}
          downloadingId={downloadingId}
          handleDownload={handleDownload}
          handleDelete={(fileId, index) =>
            handleChangeWithDelete(fileId, index, vesselId, ctoId)
          }
          getFileIcon={getFileIcon}
          maxFileSize={MAX_FILE_SIZE}
        />
      </div>
      {hasExceededFileLimit && <FileAttachErrorBox maxFiles={MAX_FILES} />}
    </div>
  );
};
